// PixelArt — requests + displays generated pixel art for a subject, with a clean
// placeholder while it generates. Art is cached on the server forever, so after the
// first generation it's instant. Live updates also arrive via the WS `art` map
// (passed in as liveUrl) so every player at the table sees the same image appear.
// When the server reports the subject failed (no API key, generation error), or
// polling exhausts its ceiling, we degrade to an initial-letter tile with a
// click-to-retry affordance instead of spinning forever.
import { useEffect, useState } from "react";
import { api } from "../lib/api";

type ArtBody = Parameters<typeof api.art>[0];
// The server may flag a subject as definitively failed (failed+reason); the
// shared api type doesn't carry those fields yet, so widen it locally.
type ArtStatus = Awaited<ReturnType<typeof api.art>> & { failed?: boolean; reason?: string };

const MAX_TRIES = 45;        // ~4 min polling ceiling
const MAX_FETCH_ERRORS = 5;  // transient network blips tolerated before giving up

export function PixelArt({
  body, liveUrl, className, alt, shape = "square",
}: {
  body: ArtBody;
  liveUrl?: string;          // url pushed via WS art map (wins if present)
  className?: string;
  alt?: string;
  // "thumb" = a 28px avatar chip for the party rail / combat tracker (reuses the same
  // cached character art as the portrait, just rendered small).
  shape?: "square" | "scene" | "thumb";
}) {
  const [url, setUrl] = useState<string | null>(liveUrl ?? null);
  const [failed, setFailed] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  // A thumb also carries the C5 `.portrait-thumb` hook and a hard 28px box so it stays
  // compact even before the design-system styles land.
  const isThumb = shape === "thumb";
  const thumbClass = isThumb ? " portrait-thumb" : "";
  const thumbStyle = isThumb
    ? ({ width: 28, height: 28, flex: "0 0 28px", borderRadius: 6, overflow: "hidden" } as const)
    : undefined;

  // Adopt a live WS url immediately — even after a failure, since another
  // client's poll can complete the generation this component gave up on.
  useEffect(() => {
    if (liveUrl) { setUrl(liveUrl); setFailed(false); }
  }, [liveUrl]);

  // Request art once per subject; poll until it's ready or definitively failed.
  // Character art can take a while (the equipment-aware avatar flow does a base
  // render + a loadout render + a vision consistency check + possible retries —
  // up to a few minutes), so poll patiently with a gentle backoff.
  useEffect(() => {
    if (liveUrl) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let tries = 0;
    let fetchErrors = 0;
    const schedule = () => {
      // ~4 min ceiling: fast polls early (catch cached/fast items), then ease off.
      const delay = tries < 8 ? 2500 : tries < 30 ? 5000 : 8000;
      if (tries < MAX_TRIES) timer = setTimeout(ask, delay);
      else setFailed(true); // exhausted — show the fallback, not an eternal spinner
    };
    const ask = async () => {
      if (!alive) return;
      try {
        const r = (await api.art(body)) as ArtStatus;
        if (!alive) return;
        if (r.ready && r.url) { setUrl(r.url); return; }
        if (r.failed) { setFailed(true); return; } // server says: not happening
        tries += 1;
        schedule();
      } catch {
        if (!alive) return;
        // Transient fetch error (server restarting, network blip) — keep polling
        // with backoff rather than dying permanently on the first hiccup.
        fetchErrors += 1;
        tries += 1;
        if (fetchErrors > MAX_FETCH_ERRORS) { setFailed(true); return; }
        schedule();
      }
    };
    ask();
    return () => { alive = false; if (timer !== undefined) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(body), liveUrl, retryNonce]);

  if (failed && !url) {
    const initial = (alt ?? body.name ?? "").trim().charAt(0).toUpperCase() || "?";
    return (
      <div
        className={`pixel-art pixel-art--${shape} pixel-art--failed${thumbClass} ${className ?? ""}`}
        style={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", ...thumbStyle }}
        title="Art unavailable — click to retry"
        onClick={() => { setFailed(false); setRetryNonce((n) => n + 1); }}
      >
        <span style={{ opacity: 0.45, fontWeight: 700, fontSize: isThumb ? "0.85em" : "1.4em" }} aria-hidden>
          {initial}
        </span>
      </div>
    );
  }
  return (
    <div className={`pixel-art pixel-art--${shape}${url ? "" : " pixel-art--loading"}${thumbClass} ${className ?? ""}`}
      style={thumbStyle}>
      {url ? (
        <img src={url} alt={alt ?? "pixel art"} loading="lazy"
          style={isThumb ? { width: "100%", height: "100%", objectFit: "cover" } : undefined} />
      ) : (
        <div className="pixel-art__ph"><span className="pixel-art__spin">◆</span></div>
      )}
    </div>
  );
}
